/* eslint-disable no-plusplus, @typescript-eslint/no-explicit-any,
   @typescript-eslint/no-unused-vars, no-console, class-methods-use-this,
   no-restricted-syntax, max-classes-per-file */

import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';

const {
  CKEditor: {
    Plugin,
    Range: CKRange,
    Position: CKPosition,
    Element: CKElement,
    Text: CKText,
    Editor,
    DataController: _DataController,
    DocumentFragment: _DocumentFragment,
  },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

/* -------------------------------------------------------------------------- */
/*  Type aliases extracted from the runtime objects                           */
/* -------------------------------------------------------------------------- */

type DocumentFragment = InstanceType<typeof _DocumentFragment>;
type Position = InstanceType<typeof CKPosition>;
type Range = InstanceType<typeof CKRange>;
type Element = InstanceType<typeof CKElement>;
type TextNode = InstanceType<typeof CKText>;
type Node = Element | TextNode;
type RootElement = Exclude<
  ReturnType<typeof Editor.prototype.model.document.getRoot>,
  null
>;
type DataController = InstanceType<typeof _DataController>;
interface InsertChange {
  type: 'insert';
  position: Position;
  html: string;
}
interface DeleteChange {
  type: 'delete';
  range: Range;
}
type CKChange = InsertChange | DeleteChange;

/* -------------------------------------------------------------------------- */
/*  Plugin                                                                     */
/* -------------------------------------------------------------------------- */

export class DiffPlugin extends Plugin {
  static get pluginName() {
    return 'DiffPlugin';
  }

  public diff(changedHTML: string) {
    const { model, data } = this.editor;
    const root = model.document.getRoot();

    if (!root) {
      throw new Error('Root element not found');
    }

    const currentHTML = this.editor.getData();
    const currentModel = data.parse(currentHTML);

    const changedFrag = data.parse(changedHTML);

    return this.diffModelFragments(currentModel, changedFrag, data, root, []);
  }

  private diffModelFragments(
    oldFrag: Element | DocumentFragment, // root clone or fragment
    newFrag: Element | DocumentFragment,
    data: DataController,
    root: Element,
    current_path: number[],
  ): CKChange[] {
    const oldChildren = Array.from(oldFrag.getChildren()) as Node[];
    const newChildren = Array.from(newFrag.getChildren()) as Node[];

    const longestSubsequencePairs = this.computeLongestCommonSubsequence(
      oldChildren,
      newChildren,
      this.nodeElementKey.bind(this),
    );

    let currentOldIndex = 0;
    let currentNewIndex = 0;
    let liveIndex = 0;

    const changes: CKChange[] = [];

    for (const [keepOldIndex, keepNewIndex] of longestSubsequencePairs) {
      const numberOfNodesToDelete = keepOldIndex - currentOldIndex;
      const numberOfNodesToInsert = keepNewIndex - currentNewIndex;

      if (numberOfNodesToDelete > 0) {
        const startingPosition = this.editor.model.createPositionFromPath(
          root,
          [...current_path, liveIndex],
        );

        const endingPosition = startingPosition.getShiftedBy(
          numberOfNodesToDelete,
        );

        changes.push({
          type: 'delete',
          range: this.editor.model.createRange(
            startingPosition,
            endingPosition,
          ),
        });
        liveIndex += numberOfNodesToDelete;
        currentOldIndex += numberOfNodesToDelete;
      }

      if (numberOfNodesToInsert > 0) {
        const startingPosition = this.editor.model.createPositionFromPath(
          root,
          [...current_path, liveIndex],
        );

        const nodesToInsert = newChildren.slice(
          currentNewIndex,
          currentNewIndex + numberOfNodesToInsert,
        );

        const html = this.editor.model.change((writer) => {
          const fragment = writer.createDocumentFragment();
          for (const node of nodesToInsert) {
            writer.append(node._clone(true), fragment); // deep-clone node into fragment
          }
          return data.stringify(fragment); // one clean HTML string
        });

        changes.push({
          type: 'insert',
          position: startingPosition,
          html,
        });

        currentNewIndex += numberOfNodesToInsert;
        liveIndex += numberOfNodesToInsert;
      }

      liveIndex++;
      currentOldIndex++;
      currentNewIndex++;
    }

    if (currentOldIndex < oldChildren.length) {
      const numberOfNodesToDelete = oldChildren.length - currentOldIndex;

      const startingPosition = this.editor.model.createPositionFromPath(root, [
        ...current_path,
        liveIndex,
      ]);

      const endingPosition = startingPosition.getShiftedBy(
        numberOfNodesToDelete,
      );

      changes.push({
        type: 'delete',
        range: this.editor.model.createRange(startingPosition, endingPosition),
      });

      liveIndex += numberOfNodesToDelete;
      currentOldIndex += numberOfNodesToDelete;
    }

    if (currentNewIndex < newChildren.length) {
      const numberOfNodesToInsert = newChildren.length - currentNewIndex;

      const startingPosition = this.editor.model.createPositionFromPath(root, [
        ...current_path,
        liveIndex,
      ]);

      const nodesToInsert = newChildren.slice(
        currentNewIndex,
        currentNewIndex + numberOfNodesToInsert,
      );

      const html = this.editor.model.change((writer) => {
        const fragment = writer.createDocumentFragment();
        for (const node of nodesToInsert) {
          writer.append(node._clone(true), fragment); // deep-clone node into fragment
        }
        return data.stringify(fragment); // one clean HTML string
      });

      changes.push({
        type: 'insert',
        position: startingPosition,
        html,
      });

      currentNewIndex += numberOfNodesToInsert;
      liveIndex += numberOfNodesToInsert;
    }

    return changes;
  }

  /**
   * Computes LCS pairs for the given node arrays
   */
  private computeLongestCommonSubsequence(
    oldNodes: Node[],
    newNodes: Node[],
    nodeKey: (node: Node) => string,
  ): Array<[number, number]> {
    const oldKeys = oldNodes.map(nodeKey);
    const newKeys = newNodes.map(nodeKey);
    return this.lcs(oldKeys, newKeys);
  }

  /* ---------------------------------------------------------------------- */
  /*  Helpers                                                               */
  /* ---------------------------------------------------------------------- */

  private nodeElementKey = (node: Node): string => {
    /**
     * There are two types of equality to care about, the first is a "structural" equality
     * i.e Does this node have the right type and the right attributes to be considered equal
     *
     * the second is value based, which should only apply to text nodes and can be handled on a case by case basis
     */

    const attributesStr = Array.from(node.getAttributes())
      .sort((a, b) => a[0].localeCompare(b[0]))
      // Dirty hack to ensure that we are not using the ids as comparisons as they don't effect the actual content
      .filter(([key]) => !key.endsWith('Id'))
      .map(([key, value]) => `${key}: ${value}`)
      .join(' ');

    if (node.is('$text')) {
      return `$text:$:${this.simpleHash(attributesStr)}::${this.simpleHash(node.data)} `;
    }

    const childrenStr = Array.from(node.getChildren())
      .map((child) => this.nodeElementKey(child as Node))
      .join(' ');

    return `${node.name} ${this.simpleHash(attributesStr)}::${this.simpleHash(childrenStr)} `;
  };

  private simpleHash(str: string): string {
    let h = 0;
    for (let i = 0; i < str.length; ++i)
      // eslint-disable-next-line no-bitwise
      h = ((h << 5) - h + str.charCodeAt(i)) | 0; // tiny JS hash
    return h.toString(36);
  }

  /**
   * Finds the Longest Common Subsequence (LCS) between two arrays of strings
   * Returns an array of index pairs representing matching elements
   * @param firstArray - First array to compare
   * @param secondArray - Second array to compare
   * @returns Array of tuples [indexInFirst, indexInSecond] for matching elements
   */
  private lcs(
    firstArray: string[],
    secondArray: string[],
  ): Array<[number, number]> {
    const firstLength = firstArray.length;
    const secondLength = secondArray.length;

    // Create a 2D DP table to store LCS lengths
    // dp[i][j] represents the length of LCS for firstArray[i:] and secondArray[j:]
    const dpTable = Array.from({ length: firstLength + 1 }, () =>
      Array<number>(secondLength + 1).fill(0),
    );

    // Build the DP table from bottom-right to top-left
    for (let i = firstLength - 1; i >= 0; i--) {
      for (let j = secondLength - 1; j >= 0; j--) {
        if (firstArray[i] === secondArray[j]) {
          // If elements match, LCS length is 1 + LCS of remaining elements
          dpTable[i][j] = 1 + dpTable[i + 1][j + 1];
        } else {
          // If no match, take the maximum LCS from either array
          dpTable[i][j] = Math.max(
            dpTable[i + 1][j], // Skip element from first array
            dpTable[i][j + 1], // Skip element from second array
          );
        }
      }
    }

    // Reconstruct the LCS by traversing the DP table
    const matchingPairs: Array<[number, number]> = [];
    let firstIndex = 0;
    let secondIndex = 0;

    while (firstIndex < firstLength && secondIndex < secondLength) {
      if (firstArray[firstIndex] === secondArray[secondIndex]) {
        // Found a matching element, add to result
        matchingPairs.push([firstIndex, secondIndex]);
        firstIndex++;
        secondIndex++;
      } else if (
        dpTable[firstIndex + 1][secondIndex] >=
        dpTable[firstIndex][secondIndex + 1]
      ) {
        // Better to skip element from first array
        firstIndex++;
      } else {
        // Better to skip element from second array
        secondIndex++;
      }
    }

    return matchingPairs;
  }
}
